import { Menu } from './menu';
import { Security } from '../containers/Authentication/reducer';

export namespace Api {

	export enum DriveItemType {
		Directory = 0,
		Component = 1,
		File = 2,
		Template = 3,
		Image = 4,
		Movie = 5,
		Publication = 6,
		Sound = 7,
		V2Gateway = 8,
		Link = 9,
		AppContainer = 10,
		Gallery = 11,
		ExternalLink = 12,
	}

	export interface DriveItemResource {
		id: number;
		block: number;
		type: DriveItemType;
	}

	// TODO: separate typing by type, example, it seems only ExternalLink can have an url attribute
	export interface DriveItem {
		id: number;
		resolution?: string;
		label: string;
		name: string;
		originalFile: string;
		type: DriveItemType;
		src: string;
		version: number;
		icon?: string;
		titles: { [k: string]: string };
		associatedResolutions?: { [k: number]: string }; // sometimes not present in the api response
		parent: number;
		descriptions: { [k: string]: string };
		originalFileSize?: number;
		creationDate: string;
		lastUpdateDate: string;
		endDate?: string;
		pages?: number;
		url?: string;
		thumbsPath?: string;
		format?: string;
	}

	// same as driveItem but delivered by meta.json API
	export interface DriveFolderInfo extends DriveItem {
		blocksNumber?: number;
		resolutions?: { [k: string]: ResolutionGroup };
		resources?: DriveItemResource[];
		resolutionsBlock?: number;
	}

	export interface ResolutionSize {
		box: boolean;
		extension: string;
		width: number;
		height: number;
		ignoreUpscale: boolean;
	}

	export interface ResolutionGroup {
		sizes: ResolutionSize[];
		supported: string[];
	}

	export interface DriveItemContentBlock {
		metas: { [k: number]: DriveItem };
		resolutions?: { [k: string]: ResolutionGroup };
	}

	export enum OutcomingType {
		Directory = 'DIRECTORY',
		Component = 'COMPONENT',
		File = 'FILE',
		Template = 'TEMPLATE',
		Image = 'IMAGE',
		Movie = 'MOVIE',
		Publication = 'PUBLICATION',
		Sound = 'SOUND',
		V2Gateway = 'V2GATEWAY',
		Link = 'LINK',
		AppContainer = 'APP_CONTAINER',
		Gallery = 'GALLERY',
		ExternalLink = 'EXTERNAL_LINK'
	}

	export interface Outcoming {
		external?: boolean;
		id: number;
		type: OutcomingType;
		version: string;
		outcomings?: Outcoming[];
		resource?: Outcoming;
	}

	export interface Config {
		applicationGallery: number;
		applicationUrl: string;
		clientId: number;
		locale: string;
		mediaPath?: string;
		service?: Service;
		downloadAllPopup?: DownloadAllPopupConfig;
	}

	export interface Template {
		commonStyles?: CommonStyles;
		sharedMethods?: ShareMethod[];
		brand?: BrandInfos;
		welcomepage?: WelcomePage;
		locale?: string;
		menu?: Menu;
		applicationSplash?: ApplicationSplash;
	}

	export interface ApplicationSplash {
		background?: string;
		image?: string;
		spinnerWheelColor?: string;
	}

	export interface WelcomePage {
		url: string;
	}

	export interface BrandInfos {
		url: string;
		name: string;
		logo: string;
		logoHome: string;
	}

	export interface DownloadAllPopupConfig {
		enabled: boolean;
	}

	export interface CommonStyles {
		ontouch?: TouchStyle;
		primary?: PrimaryStyle;
		pushMenu?: PushMenuStyle;
		secondary?: SecondaryStyle;
	}

	export interface PrimaryStyle {
		actionColor: string;
		backgroundColor: string;
		cardColor: string;
		contentColor: string;
		spacerColor: string;
		strongColor: string;
		topnavColor: string;
		iconsColor: string;
		topnavOpacity: number;
	}

	export interface SecondaryStyle {
		activeColor: string;
		alertColor: string;
		altContentColor: string;
		altPopupColor: string;
		notificationColor: string;
	}

	export interface TouchStyle {
		primaryBackgroundColor: string;
		primaryCardColor: string;
		primaryContentColor: string;
		pushmenuBackgroundColor: string;
		secondaryAlertColor: string;
	}

	export interface PushMenuStyle {
		activeColor: string;
		backgroundColor: string;
		contentColor: string;
		iconColor: string;
		spacerColor: string;
		strongBackgroundColor: string;
		strongColor: string;
	}

	export interface Service {
		host: string;
		token: string;
		email: EmailService;
		download: DownloadService;
	}

	export interface EmailService {
		service: string;
	}

	export interface DownloadService {
		service: string;
	}

	export enum ShareMethod {
		Download = 'download',
		Share = 'share'
	}

	enum AssetItemAccessType {
		PUBLIC = 'PUBLIC',
	}

	enum AssetItemInvoiceStatus {
		CHARMED = 'CHARMED',
	}

	enum AssetItemStatus {
		LIVE = 'LIVE',
	}

	export enum AssetItemFileTypes {
		DIRECTORY = 'DIRECTORY',
		COMPONENT = 'COMPONENT',
		GALLERY = 'GALLERY',
		IMAGE = 'IMAGE',
		PUBLICATION = 'PUBLICATION',
		TEMPLATE = 'TEMPLATE',
		MOVIE = 'MOVIE',
		FILE = 'FILE',
		V2GATEWAY = 'V2GATEWAY',
		EXTERNAL_LINK = 'EXTERNAL_LINK',
		APP_CONTAINER = 'APP_CONTAINER',
		LINK = 'LINK',
		DEFAULT = 'DEFAULT',
	}

	export interface AspDriveResourceItem {
		accessType: AssetItemAccessType;
		beginDate: {
			date: number;
			month: number;
			year: number;
		};
		clientId: number;
		clonable: boolean;
		endDate: number | null;
		expired: boolean;
		filesize: number;
		folder: boolean;
		forbidUpdateDate: number | null;
		forbiddenUpdate: boolean;
		genericItemCreationDate: number;
		genericItemId: number;
		genericItemLastUpdate: number;
		genericItemName: string;
		globalId: number;
		iconName: string | null;
		invoiceStatus: AssetItemInvoiceStatus;
		label: string;
		modelId: number;
		modelName: string;
		parentDriveGId: number | null;
		position: number;
		pushable: boolean;
		referent: AspDriveResourceItem | null;
		relUrl: string;
		secured: boolean;
		size: number;
		status: AssetItemStatus;
		type: AssetItemFileTypes;
		version: number;
	}

	export interface AppLoginResponse {
		login: string;
		clientId: number;
		associatedApps: AspDriveResourceItem[];
	}

	export interface AuthMethodsResponse {
		orgAuthMethods: Security[];
	}

	enum CustomerContextRoles {
		ROLE_PRODUCTION = 'ROLE_PRODUCTION',
		ROLE_COMMERCIAL_SALESFORCE = 'ROLE_COMMERCIAL_SALESFORCE',
		ROLE_COMMERCIAL_MANUAL = 'ROLE_COMMERCIAL_MANUAL',
		ROLE_AGENCY = 'ROLE_AGENCY',
		ROLE_SAAS_ACCOUNT_MANAGER = 'ROLE_SAAS_ACCOUNT_MANAGER',
		ROLE_SAAS_USER = 'ROLE_SAAS_USER',
		ROLE_CONSUMER = 'ROLE_CONSUMER',
	}

	export interface CustomerAccount {
		autoRenew: boolean;
		availableCredits: number;
		company: string;
		creationDate: number;
		email: string;
		id: number;
		label: string;
		licenceId: number;
		locale: string;
		parentId: number;
		phone: string;
		role: CustomerContextRoles;
		saleName: string;
		status: 'ACTIVE' | 'DISABLED';
		type: string;
	}

	export interface GetClosestServerResponse {
		servers: { [k: string]: ServerInfos };
	}

	export interface ServerInfos {
		domain: string;
		closestServer: boolean;
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/typings/api.ts